// ── CallRegister 조회 API 묶음 ───────────────────────────────────────────────
// useCallRegisterHandlers.js 가 300줄 규칙에 걸려 조회 함수만 그대로 옮겼다 (2026-09-20).
// 동작 변화 없음 — fetchOptions / fetchMyRole / fetchSalesPersons / fetchPrefs / upsertPref / fetchList
// 2026-10-13(3단계 5번) integrated = 통합 일일콜 메뉴: 상품은 고른 회사(companyCode)로 `${adminApiBase}/ticket-types?company=`,
//   마케팅 담당자·담당 고정은 메뉴 주소. 그 밖의 화면은 그대로.
// 2026-10-23 담당 TM 후보(fetchOwnerOptions)는 모든 관리 화면이 서버 `${adminApiBase}/call-owners`(재직 + 일일콜 열쇠)에서 받는다.
import { apiClient } from '../../../api/apiClient';

export function createCallRegisterFetchers(core, {
  adminMode, adminApiBase, scope, companyCode, showToast, integrated = false,
}) {
  const {
    setLoading, setTicketTypes, setIsExternalWorker, setExternalCategoryName,
    setIsExtCategoryFixed, setSalesPersonNames, setList, setForm,
  } = core;

  // ── API: 상품 목록 + 외부 카테고리명 조회 ─────────────────────────────────
  async function fetchOptions() {
    // companyCode + adminMode: 회사별 전용 엔드포인트
    // adminMode(일반): /api/admin/ticket-types
    // 직원: /api/tm/ticket-types (external_category_name 포함)
    // companyCode가 있으면 관리자/직원 구분 없이 회사별 전용 엔드포인트 사용
    // → is_category 값을 받아 표시 방식(상품/카테고리) 결정에 사용
    // 통합 메뉴는 회사를 고르기 전엔 상품을 읽지 않는다(회사마다 상품·분류 규칙이 다르다)
    if (integrated && !companyCode) {
      setIsExtCategoryFixed(false);
      setExternalCategoryName('');
      setTicketTypes([]);
      return undefined;
    }
    const endpoint = integrated
      ? `${adminApiBase}/ticket-types?company=${encodeURIComponent(companyCode)}`
      : companyCode
        ? `${adminApiBase}/ticket-types`
        : adminMode ? '/api/admin/ticket-types' : '/api/tm/ticket-types';
    const r = await apiClient.get(endpoint);
    if (!r.ok) return showToast('상품 목록 조회에 실패했습니다.', 'error');
    const rows = Array.isArray(r.data?.data) ? r.data.data : [];

    // companyCode가 있으면 관리자/직원 모두 동일 로직: is_category 확인 후 분기
    if (companyCode) {
      const allActive = rows.filter((t) => t?.is_active !== false);
      const extCat = String(r.data?.external_ticket_type || '').trim();
      const isCategory = Boolean(r.data?.is_category);
      setIsExtCategoryFixed(isCategory);
      if (isCategory) {
        // 카테고리명 고정 모드: ticket_type 필드에 카테고리명 표시 (ticket_type_id=null)
        setExternalCategoryName(extCat);
        setTicketTypes(allActive);
      } else {
        // 개별 상품 선택 모드: 해당 카테고리 소속 상품만 드롭다운에 표시
        setExternalCategoryName(''); // 고정 해제 → useEffect이 ticket_type을 덮어쓰지 않음
        const catProducts = extCat
          ? allActive.filter((t) => t.category === extCat)
          : allActive;
        setTicketTypes(catProducts);
        // 첫 번째 상품 자동 선택
        if (catProducts.length > 0) {
          setForm((prev) => ({ ...prev, ticket_type_id: catProducts[0].id, ticket_type: '' }));
        }
      }
    } else if (adminMode) {
      const allActive = rows.filter((t) => t?.is_active !== false);
      const extCat = String(r.data?.external_ticket_type || '').trim();
      if (scope === 'jeju' && extCat) {
        // 제주 관리: 제주(내부) 카테고리 상품만 표시
        setTicketTypes(allActive.filter((t) => t.category !== extCat));
      } else {
        setTicketTypes(allActive);
      }
    } else {
      // 일반 직원 모드 (companyCode 없음)
      setTicketTypes(rows);
      const catName = String(r.data?.external_category_name || '').trim();
      setExternalCategoryName(catName);
      // 표시 방식은 상품 관리 > "회사에 붙이기" 가 정한다. 붙여둔 분류가 없으면 서버가 false 를 준다.
      core.setIsExtCategoryFixed(Boolean(r.data?.is_category));
    }
  }

  /** 내 역할 조회: 외부직원 여부 설정 */
  async function fetchMyRole() {
    const r = await apiClient.get('/api/me');
    if (!r.ok) return;
    const workerType = String(r.data?.user?.workerType || '').trim();
    setIsExternalWorker(workerType === 'external');
  }

  /** 마케팅 담당자 이름 목록 조회 */
  async function fetchSalesPersons() {
    const r = await apiClient.get(integrated ? `${adminApiBase}/sales-persons` : '/api/tm/sales-persons');
    if (!r.ok) return;
    const rows = Array.isArray(r.data?.data) ? r.data.data : [];
    const names = rows.map((row) => String(row?.name || '').trim()).filter(Boolean);
    setSalesPersonNames(Array.from(new Set(names)));
  }

  /** 사용자 설정(담당자 고정 여부, 이름) 조회 */
  async function fetchPrefs() {
    const r = await apiClient.get(integrated ? `${adminApiBase}/preferences` : '/api/tm/preferences');
    if (!r.ok) return;
    const pref = r.data?.preferences || {};
    core.setFixedSalesPerson(Boolean(pref.fixed_enabled));
    const name = String(pref.sales_person_name || '');
    if (name) setForm((p) => ({ ...p, sales_person_name: name }));
  }

  /** 사용자 설정 저장 (담당자 고정 여부 + 이름) */
  async function upsertPref(fixed, name) {
    const r = await apiClient.put(integrated ? `${adminApiBase}/preferences` : '/api/tm/preferences', { fixed_enabled: fixed, sales_person_name: name });
    return Boolean(r.ok);
  }

  /** 콜 목록 조회 */
  async function fetchList() {
    setLoading(true);
    // adminMode: scope에 따른 전용 엔드포인트, 직원: 본인 콜만
    //   직원 화면도 회사별 메뉴라, 어느 회사 목록인지 알려 그 회사 콜만 받는다.
    const myListUrl = companyCode
      ? `/api/tm/calls?call_company_code=${encodeURIComponent(companyCode)}`
      : '/api/tm/calls';
    const r = await apiClient.get(adminMode ? `${adminApiBase}/calls` : myListUrl);
    setLoading(false);
    if (!r.ok) return setList([]);
    const rows = Array.isArray(r.data?.data) ? r.data.data : [];
    setList(rows.map((row) => ({ ...row, mobile_phone: row.mobile_phone || '' })));
  }

  /** 관리 화면 '담당자' 칸 후보 = 재직 + 이 화면 회사의 일일콜 열쇠(서버 call-owners — 2026-10-23, 모든 관리 화면 같은 규칙).
   *  전엔 통합 메뉴만 회사 재직 직원 전부(마케터 포함)를 받고, 그 밖의 화면은 콜 목록에서 '콜 건 사람'을 뽑아 퇴사자가 남았다.
   *  company_code: 통합 메뉴는 담당자를 고르면 등록할 회사를 그 사람 회사로 맞춘다(CallRegister, 2026-10-14). */
  async function fetchOwnerOptions() {
    if (!adminMode) return;
    const r = await apiClient.get(`${adminApiBase}/call-owners`);
    const rows = r.ok && Array.isArray(r.data?.data) ? r.data.data : [];
    core.setOwnerOptions(rows.map((u) => ({
      id: Number(u.id), name: String(u.name || '').trim() || '(이름없음)',
      company_code: String(u.company_code || '').trim().toUpperCase(),
    })));
  }

  return { fetchOptions, fetchMyRole, fetchSalesPersons, fetchPrefs, upsertPref, fetchList, fetchOwnerOptions };
}
